import { Asset } from 'components/common/types/Asset.types';
import { PlannerMapDataElement } from 'components/common/types/Planner.types';

type PickingObject = { geometry?: unknown; assets?: Asset[]; frameId?: string; longitude?: number };

export type GroupableAsset = Pick<Asset, 'furnitureCode' | 'longitude' | 'latitude'>;

export const areAllAssetsGrouped = (assets: GroupableAsset[]): boolean => {
  if (assets.length === 0) return false;

  const [{ furnitureCode, longitude, latitude }] = assets;

  return assets.every(
    (asset) =>
      (furnitureCode && asset.furnitureCode === furnitureCode) ||
      (longitude === asset.longitude && latitude === asset.latitude),
  );
};

const matchesFirstAsset = (firstAsset: GroupableAsset, nextAsset: GroupableAsset): boolean =>
  (Boolean(firstAsset.furnitureCode) && nextAsset.furnitureCode === firstAsset.furnitureCode) ||
  (firstAsset.longitude === nextAsset.longitude && firstAsset.latitude === nextAsset.latitude);

const getLocationKey = ({ longitude, latitude }: GroupableAsset): string => `${longitude},${latitude}`;

export const groupAssetsByFurnitureOrLocation = (assets: GroupableAsset[]): GroupableAsset[][] => {
  if (assets.length === 0) return [];
  if (areAllAssetsGrouped(assets)) return [assets];

  const groups: GroupableAsset[][] = [];
  const groupIndexByFurniture = new Map<string, number>();
  const groupIndexByLocation = new Map<string, number>();

  assets.forEach((asset) => {
    const { furnitureCode } = asset;

    if (furnitureCode) {
      const furnitureGroupIndex = groupIndexByFurniture.get(furnitureCode);

      if (furnitureGroupIndex !== undefined && matchesFirstAsset(groups[furnitureGroupIndex][0], asset)) {
        groups[furnitureGroupIndex].push(asset);
        return;
      }
    }

    const locationKey = getLocationKey(asset);
    const locationGroupIndex = groupIndexByLocation.get(locationKey);

    if (locationGroupIndex !== undefined && matchesFirstAsset(groups[locationGroupIndex][0], asset)) {
      groups[locationGroupIndex].push(asset);
      return;
    }

    const newGroupIndex = groups.length;
    groups.push([asset]);

    if (furnitureCode) {
      groupIndexByFurniture.set(furnitureCode, newGroupIndex);
    }

    groupIndexByLocation.set(locationKey, newGroupIndex);
  });

  return groups;
};

const hasClickableProperties = (props: { frameId?: string; longitude?: number }): boolean =>
  props.frameId !== undefined || props.longitude !== undefined;

export const isClusterClickable = (cluster: PlannerMapDataElement | null | undefined): boolean => {
  if (!cluster?.properties) return false;

  const { assets } = cluster.properties;
  return assets && assets.length > 0 ? areAllAssetsGrouped(assets) : hasClickableProperties(cluster.properties);
};

export const isObjectClickable = (object: PickingObject | null | undefined): boolean => {
  if (!object || object.geometry || object.assets === undefined) return false;
  return object.assets.length > 0 ? areAllAssetsGrouped(object.assets) : hasClickableProperties(object);
};
